import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FacebookLinkManagement.css';

const API_URL = 'https://apipurple.topranohotelandlounge.com';

const FacebookLinkManagement = ({ onUpdate }) => {
  const [facebookLinks, setFacebookLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [editLink, setEditLink] = useState(null);
  const [formData, setFormData] = useState({ description: '', video: '' });

  // Fetch all Facebook links
  useEffect(() => {
    fetchFacebookLinks();
  }, []);

  const fetchFacebookLinks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/facebooklink`);
      setFacebookLinks(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching Facebook links:', err.message);
      setError('Failed to load Facebook links. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editLink || !editLink._id) {
      setError('Invalid Facebook link selected.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.put(
        `${API_URL}/api/facebooklink/${editLink._id}`,
        formData
      );
      setFacebookLinks(facebookLinks.map(link =>
        link._id === editLink._id ? response.data : link
      ));
      setEditLink(null);
      setFormData({ description: '', video: '' });
      setMessage('Facebook link updated successfully!');
      if (onUpdate) onUpdate('Facebook link updated successfully!');
    } catch (err) {
      console.error('Error updating Facebook link:', err);
      setError(err.response?.data?.error || 'Failed to update Facebook link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (_id) => {
    if (!window.confirm('Are you sure you want to delete this Facebook link?')) return;
    setError(null);
    setMessage(null);
    try {
      await axios.delete(`${API_URL}/api/facebooklink/${_id}`);
      setFacebookLinks(facebookLinks.filter(link => link._id !== _id));
      setMessage('Facebook link deleted successfully!');
      if (onUpdate) onUpdate('Facebook link deleted successfully!');
    } catch (err) {
      console.error('Error deleting Facebook link:', err);
      setError(err.response?.data?.error || 'Failed to delete Facebook link. Please try again.');
    }
  };

  // Start editing a link
  const startEditing = (link) => {
    setEditLink(link);
    setFormData({
      description: link.description,
      video: link.rawVideo || '' // Use rawVideo for editing
    });
    setError(null);
    setMessage(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditLink(null);
    setFormData({ description: '', video: '' });
    setError(null);
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span className="loading-text">Loading Facebook Links...</span>
      </div>
    );
  }

  return (
    <div className="facebook-management">
      {message && (
        <div className="alert alert-success">
          {message}
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}
      
      <h2 className="list-title">
        Manage Facebook Links ({facebookLinks.length})
      </h2>
      
      <div className="list-container">
        {facebookLinks.length === 0 ? (
          <div className="empty-state">
            <p>No Facebook links found.</p>
          </div>
        ) : (
          facebookLinks.map(link => (
            <div key={link._id} className="list-item">
              <div className="list-item-header">
                <h3 className="list-item-title">
                  {link.id}
                </h3>
                <div className="list-item-actions">
                  <button
                    onClick={() => startEditing(link)}
                    className="btn btn-primary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(link._id)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <p className="list-item-content">
                {link.description}
              </p>
              
              {link.video && (
                <div className="list-item-video">
                  <h4 className="video-label">Video Preview:</h4>
                  <iframe
                    src={link.video}
                    title={link.id}
                    className="video-iframe"
                    allowFullScreen
                    frameBorder="0"
                  />
                </div>
              )}
              
              <div className="list-item-meta">
                <span className="meta-item">Created: {new Date(link.createdAt).toLocaleDateString()}</span>
                <span className="meta-item">Updated: {new Date(link.updatedAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {editLink && (
        <div className="admin-form">
          <h3 className="form-title">
            Edit Facebook Link: {editLink.id}
          </h3>
          
          <form onSubmit={handleEditSubmit} className="form-container">
            <div className="form-group">
              <label className="form-label">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-textarea"
                required
                placeholder="Enter description..."
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Facebook Video URL (optional)
              </label>
              <input
                type="url"
                value={formData.video}
                onChange={(e) => setFormData({ ...formData, video: e.target.value })}
                className="form-input"
                placeholder="https://www.facebook.com/your-video-url"
              />
              <p className="input-hint">
                Paste the full Facebook video URL here
              </p>
            </div>
            
            {formData.video && (
              <div className="form-group">
                <label className="form-label">
                  Video Preview
                </label>
                <div className="video-preview-container">
                  <iframe
                    src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(formData.video)}`}
                    title="Video Preview"
                    className="video-preview-iframe"
                    allowFullScreen
                    frameBorder="0"
                  />
                </div>
              </div>
            )}
            
            <div className="form-buttons">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FacebookLinkManagement;